
import * as LZString from "lz-string";

/**
 * Compresses and encodes an object to be stored in URL hash
 * @param data - The data to encode
 */
export function encodeHashData(data: any): string {
  try {
    // Convert to JSON string first
    const jsonString = JSON.stringify(data);
    // Compress the string to make the URL shorter
    return LZString.compressToEncodedURIComponent(jsonString);
  } catch (error) {
    console.error("Error encoding hash data:", error);
    return "";
  }
}

/**
 * Decodes and decompresses hash data back into an object
 * @param hash - The hash string to decode (without the # symbol)
 */
export function decodeHashData<T>(hash: string): T | null {
  try {
    if (!hash) return null;
    
    // Decompress the hash string
    const jsonString = LZString.decompressFromEncodedURIComponent(hash);
    if (!jsonString) return null;
    
    // Parse the JSON string back to an object
    return JSON.parse(jsonString) as T;
  } catch (error) {
    console.error("Error decoding hash data:", error);
    return null;
  }
}

/**
 * Validates form data against a validation schema or function
 * @param data - The data to validate
 * @param validator - Validation function that returns true if valid
 */
export function validateHashData<T>(
  data: T | null, 
  validator: (data: any) => boolean = () => true
): T | null {
  if (!data) return null;
  
  try {
    return validator(data) ? data : null;
  } catch (error) {
    console.error("Error validating hash data:", error);
    return null;
  }
}

/**
 * Clean empty/null values from an object before encoding
 * @param data - The object to clean
 */
export function cleanFormData<T extends Record<string, any>>(data: T): Partial<T> {
  const cleaned: Partial<T> = {};
  
  Object.entries(data).forEach(([key, value]) => {
    // Keep values that are not empty, null or undefined
    if (value !== "" && value !== null && value !== undefined) {
      cleaned[key as keyof T] = value;
    }
  });
  
  return cleaned;
}

/**
 * Update URL hash without causing a page reload
 * @param hash - The hash string to set (without the # symbol)
 */
export function updateUrlHash(hash: string): void {
  if (typeof window !== 'undefined') {
    // Use history.replaceState to update the URL without causing navigation
    const url = new URL(window.location.href);
    url.hash = hash;
    window.history.replaceState({}, '', url.toString());
  }
}

/**
 * Get current URL hash without the # symbol
 */
export function getCurrentUrlHash(): string {
  if (typeof window !== 'undefined') {
    return window.location.hash.substring(1);
  }
  return '';
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T, 
  wait: number = 500
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  
  return function(...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout !== null) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(later, wait);
  };
}
